import React, { useEffect, useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';

import { NotificationProvider } from '../../common';

import JZKConverter from './components/JZKConverter';
import JZKHistoricalData from './components/JZKHistoricalData';
import JZKOlderData from './components/JZKOlderData';
import { jzkApi } from './services/api';
import { queryClient } from './services/queryClient';
import { JZKRate, LoadingState } from './types';
import { useTranslation } from './utils/i18n';

type ViewMode = 'current' | 'historical';

const App: React.FC = () => {
  const { t } = useTranslation();
  const [rates, setRates] = useState<JZKRate[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [viewMode, setViewMode] = useState<ViewMode>('current');

  // Initialize plugin window object
  useEffect(() => {
    if (!window.cdcargoJzkPlugin) {
      window.cdcargoJzkPlugin = {
        language: { current: 'cs' },
        translations: {},
      };
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingState('loading');

        const ratesResponse = await jzkApi.getRates();
        // Prefetch validity data into React Query cache (best-effort)
        void queryClient
          .prefetchQuery({
            queryKey: ['jzk-validity'],
            queryFn: jzkApi.getValidity,
          })
          .catch(() => undefined);

        setRates(ratesResponse.rates);
        setLoadingState('success');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching JŽK data:', error);
        setLoadingState('error');
      }
    };

    fetchData();
  }, []);

  const handleShowHistorical = () => {
    setViewMode('historical');
  };

  const handleBackToCurrent = () => {
    setViewMode('current');
  };

  if (loadingState === 'loading') {
    return (
      <div className="cdcargo-jzk-app">
        <div className="jzk-loading">{t('loading.rates')}</div>
      </div>
    );
  }

  if (loadingState === 'error') {
    return (
      <div className="cdcargo-jzk-app">
        <div className="jzk-error">{t('error.loadingRates')}</div>
      </div>
    );
  }

  return (
    <div className="cdcargo-jzk-app">
      {viewMode === 'current' ? (
        <div className="jzk-main-layout">
          <JZKConverter rates={rates} />
          <JZKOlderData onShowHistorical={handleShowHistorical} />
        </div>
      ) : (
        <JZKHistoricalData onBack={handleBackToCurrent} />
      )}
    </div>
  );
};

// Wrap App with QueryClientProvider and NotificationProvider
const AppWithProviders = () => (
  <QueryClientProvider client={queryClient}>
    <NotificationProvider
      maxSnack={3}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      autoHideDuration={3000}
    >
      <App />
    </NotificationProvider>
  </QueryClientProvider>
);

export default AppWithProviders;
